/**
 * BillingSections — Payments + Documents for a lead (Phase 8d).
 *
 * Finalize (048) splits it across two tabs: view 'finalize' renders Payments and
 * the invoice list (the money lifecycle — finalize, invoice, collect — in one
 * place); view 'estimate' renders the other documents beneath the EstimatePanel
 * and points to Finalize for payments and invoicing.
 *
 * Balance math and document numbering come from the pure helpers in
 * lib/billing.ts; the live estimate total comes from calcEstimate (lib/estimate).
 */

import React, { useEffect, useRef, useState } from 'react';
import { numberInput } from '../lib/numberInput';
import { humanize } from '../lib/feedLabels';
import { useApi, useApiClient } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { formatMoney, formatDate, today } from '../lib/utils';
import { calcEstimate } from '../lib/estimate';
import { getBillableTotal, getPaidTotal, getBalance, balanceColor } from '../lib/billing';
import { isLongDistance } from '../lib/moveType';
import { DocumentsSection } from './DocumentsSection';
import { useAction, ActionButton } from '../hooks/useAction';
import { Icons } from '../lib/icons';

const COLOR: Record<string, string> = { green: 'var(--crm-green)', amber: 'var(--crm-amber)', red: 'var(--crm-red)', neutral: 'var(--crm-muted)' };
/**
 * Only reached when the config list cannot be read. It deliberately does NOT
 * contain Cash or Cheque: this is the form a rep records a CLIENT payment on,
 * and both contracts state in writing that we accept neither. The previous
 * value of this constant was the live list — the client form never read
 * config_lists at all, so editing Settings changed nothing here and the
 * dropdown offered two methods the contract refuses.
 */
const FALLBACK_METHODS = ['Credit Card', 'e-Transfer', 'Other'];
const DOC_LABEL: Record<string, string> = { work_order: 'Work Order', invoice: 'Invoice', bill_of_lading: 'Bill of Lading' };

interface BillingSectionsProps {
  leadId: string;
  lead: any;
  tariff: any;
  view: 'estimate' | 'finalize';
  /** Bump to re-read payments and documents quietly (e.g. after an invoice is issued elsewhere). */
  reloadKey?: number;
  /** After anything here changes the documents (a void, a status change). */
  onDocumentsChanged?: () => void;
  /** Estimate view: the link to the Finalize tab. */
  onGoFinalize?: () => void;
  /** After a payment write — it can close the job or clear "Square cost needed". */
  onPaymentsChanged?: () => void;
}

export function BillingSections({ leadId, lead, tariff, view, reloadKey, onDocumentsChanged, onGoFinalize, onPaymentsChanged }: BillingSectionsProps) {
  const api = useApiClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin'; // edit / delete payment are admin-only (N3)

  const { data: est } = useApi<{ estimate: any; items: any[]; accessorials?: any[] }>(async () => (await api.getEstimate(leadId)).data!, {
    dependencies: [leadId],
  });
  const { data: fees } = useApi<any[]>(async () => (await api.getFees()).data || []);
  const { data: branches } = useApi<any[]>(async () => (await api.getBranches()).data || []);
  const { data: users } = useApi<any[]>(async () => (await api.getUsers()).data || []);
  const { data: companyArr } = useApi<any[]>(async () => (await api.getCompany()).data || []);
  // What a CLIENT may pay with — its own list, not the payout list the
  // contractor and expense forms use. Seeded on first read by the backend.
  const { data: methodRows } = useApi<any[]>(async () => {
    try { return (await api.getConfigList('client_payment_method')).data || []; } catch { return []; }
  });
  const clientMethods = methodRows && methodRows.length > 0 ? methodRows.map((r: any) => String(r.value)) : FALLBACK_METHODS;
  /**
   * QUIET REFRESHES after every write (useApi.refresh): the lists stay on screen
   * until the new ones replace them, and a failed refresh keeps them rather than
   * emptying them — refetch() set data to null on error, so a blip after saving a
   * payment showed a lead with no payments at all.
   */
  const { data: payments, refresh: refreshPaymentsRaw } = useApi<any[]>(async () => (await api.getPayments(leadId)).data || [], {
    dependencies: [leadId],
  });
  const { data: documents, refresh: refreshDocumentsRaw } = useApi<any[]>(async () => (await api.getDocuments(leadId)).data || [], {
    dependencies: [leadId],
  });
  const refetchPayments = () => {
    refreshPaymentsRaw().catch((e) => console.error('Payments refresh failed:', e));
    onPaymentsChanged?.();
  };
  const refetchDocuments = () => {
    refreshDocumentsRaw().catch((e) => console.error('Documents refresh failed:', e));
    onDocumentsChanged?.();
  };
  // A reloadKey bump from the parent: quiet re-reads, nothing blanks.
  const firstLoad = useRef(true);
  useEffect(() => {
    if (firstLoad.current) { firstLoad.current = false; return; }
    refreshPaymentsRaw().catch((e) => console.error('Payments refresh failed:', e));
    refreshDocumentsRaw().catch((e) => console.error('Documents refresh failed:', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);
  // Document templates (Phase 8i.1) — filtered to this lead's move type (+ 'any').
  const moveTypeKey = isLongDistance(lead) ? 'long_distance' : 'local';
  const { data: templates } = useApi<any[]>(
    async () => { try { return (await api.getDocumentTemplates(moveTypeKey)).data || []; } catch { return []; } },
    { dependencies: [leadId, moveTypeKey], skip: view !== 'estimate' }
  );

  const [payModal, setPayModal] = useState<{ mode: 'add' | 'edit'; payment?: any } | null>(null);
  const [busy, setBusy] = useState(false);
  const [sendMsg, setSendMsg] = useState<{ ok: boolean; text: string; onOverride?: () => void } | null>(null); // inline send feedback (E3)

  const items = est?.items ?? [];
  const branch = branches?.find((b) => b.id === lead.branch_id) ?? null;
  const calc = calcEstimate(lead, items, tariff, branch, fees ?? [], est?.accessorials ?? []);
  const liveTotal = calc.total;

  const company = companyArr?.[0] ?? {};
  const ccRate = Number(company.cc_fee_rate ?? 3.0);
  // 048: the Square cost prefill — charged × rate % + fixed (Settings → Pricing).
  const squareRate = Number(company.square_fee_rate ?? 2.9);
  const squareFixed = Number(company.square_fee_fixed ?? 0.3);
  const [squareFor, setSquareFor] = useState<any | null>(null);

  // The margin is no longer here: it is the admin-only "Margin" section on the
  // Finalize tab, computed server-side (backend lib/margin.ts) and served only
  // by the admin-gated /api/finalize-admin route. Nothing on this rep-visible
  // screen reads contractor figures any more.

  const pays = payments ?? [];
  const docs = documents ?? [];
  const billable = getBillableTotal(lead, docs, liveTotal, pays);
  const paid = getPaidTotal(pays);
  const balance = getBalance(lead, docs, pays, liveTotal);
  const balColor = COLOR[balanceColor(balance, paid, lead.stage)];
  const usingInvoice = docs.some((d) => d.doc_type === 'invoice' && d.status !== 'void');

  const userName = (id: string | null | undefined) => {
    const u = users?.find((x) => x.id === id);
    return u ? `${u.first_name ?? ''} ${(u.last_name ?? '')[0] ?? ''}`.trim() : '—';
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      console.error('Billing action failed:', e);
    } finally {
      setBusy(false);
    }
  };

  // One receipt send at a time, with a key per intent (useAction).
  const receiptAction = useAction();
  const resendReceipt = (paymentId: string) =>
    receiptAction.run(async (key) => {
      setSendMsg(null);
      try {
        await api.withIdempotencyKey(key).resendPaymentReceipt(paymentId, true);
        setSendMsg({ ok: true, text: 'Receipt sent.' });
      } catch (e: any) {
        setSendMsg({ ok: false, text: e?.message || 'Receipt failed — please try again.' });
        throw e; // keep the key: a retry is the same send
      }
    }).catch(() => {});

  /**
   * Called by PaymentModal's ActionButton with that modal's idempotency key, so
   * a second click (or a retry after a dropped response) cannot record the
   * payment twice. Rethrows after showing the message so the key is kept for
   * the retry.
   */
  const savePayment = async (data: any, id: string | undefined, key: string) => {
    setBusy(true); setSendMsg(null);
    try {
      if (id) {
        await api.updatePayment(id, data);
      } else {
        const res = await api.withIdempotencyKey(key).addPayment(leadId, data);
        const paymentId = res.data?.id;
        const rec = res.data?.receipt;
        // Payment is recorded regardless; the receipt is best-effort + inline.
        if (rec?.sent) setSendMsg({ ok: true, text: 'Payment recorded — receipt emailed to the client.' });
        else if (rec?.blocked) setSendMsg({ ok: false, text: rec.error || 'Receipt blocked — this address previously bounced.', onOverride: paymentId ? () => resendReceipt(paymentId) : undefined });
        else if (rec?.error) setSendMsg({ ok: false, text: `Payment recorded, but the receipt failed to send: ${rec.error}` });
      }
      setPayModal(null);
      refetchPayments();
      refetchDocuments(); // auto-paid may have flipped an invoice
    } catch (e: any) {
      setSendMsg({ ok: false, text: e?.message || 'Could not save the payment — please try again.' });
      throw e;
    } finally { setBusy(false); }
  };

  const deletePayment = (id: string) =>
    run(async () => {
      if (!window.confirm('Delete this payment?')) return;
      await api.deletePayment(id);
      refetchPayments();
      refetchDocuments();
    });

  if (view === 'estimate') {
    return (
      <div style={{ marginTop: '26px' }}>
        <div style={{ fontSize: '12px', color: 'var(--crm-muted)', background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '9px', padding: '10px 14px' }}>
          Payments and invoicing are on the{' '}
          <button onClick={onGoFinalize} style={{ background: 'none', border: 'none', color: 'var(--crm-accent)', cursor: 'pointer', fontSize: '12px', padding: 0 }}>
            Finalize tab →
          </button>
        </div>
        <DocumentsSection
          leadId={leadId}
          documents={docs}
          templates={templates ?? []}
          isAdmin={isAdmin}
          userName={userName}
          refetch={refetchDocuments}
          refetchPayments={refetchPayments}
          scope="documents"
        />
      </div>
    );
  }

  return (
    <div style={{ marginTop: '26px' }}>
      {/* ============ PAYMENTS ============ */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ fontSize: '11px', fontWeight: 700, color: 'var(--crm-dim)', textTransform: 'uppercase' }}>Payments</h3>
        <button
          onClick={() => setPayModal({ mode: 'add' })}
          disabled={busy}
          style={{ padding: '6px 12px', background: 'var(--crm-accent-fill)', color: 'var(--crm-on-fill)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
        >
          + Add payment
        </button>
      </div>

      {/* What the last payment action did. This was set but never rendered after
          D2 (ca06791) — receipts, "Send anyway" and save errors were invisible. */}
      {sendMsg && (
        <div style={{ fontSize: '12px', color: sendMsg.ok ? 'var(--crm-green)' : 'var(--crm-red)', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
          <span>{sendMsg.text}</span>
          {sendMsg.onOverride && (
            <button onClick={sendMsg.onOverride} disabled={receiptAction.pending}
              style={{ padding: '3px 10px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-amber-line)', color: 'var(--crm-amber)', borderRadius: '6px', cursor: receiptAction.pending ? 'progress' : 'pointer', fontSize: '11px', fontWeight: 600, opacity: receiptAction.pending ? 0.6 : 1 }}>
              {receiptAction.pending ? 'Sending…' : 'Send anyway'}
            </button>
          )}
          <button onClick={() => setSendMsg(null)} style={{ ...iconBtn, fontSize: '11px' }}>dismiss</button>
        </div>
      )}

      {/* Summary strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '12px' }}>
        <SummaryCell label={usingInvoice ? 'Invoice total' : 'Estimate total'} value={formatMoney(billable)} />
        <SummaryCell label="Paid" value={formatMoney(paid)} valueColor="var(--crm-green)" />
        <SummaryCell label="Balance owing" value={formatMoney(balance)} valueColor={balColor} big />
      </div>

      {/* Payment history */}
      <div style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '10px', overflow: 'hidden', marginBottom: '10px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Date', 'Type', 'Method', 'Amount', 'CC Fee', 'Total Charged', 'Square cost', 'Notes', 'Rep', ''].map((h, i) => (
                <th
                  key={h + i}
                  style={{ textAlign: i >= 3 && i <= 6 ? 'right' : 'left', fontSize: '9px', fontWeight: 700, color: 'var(--crm-dim)', textTransform: 'uppercase', padding: '8px 10px', borderBottom: '1px solid var(--crm-line)' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pays.length === 0 && (
              <tr>
                <td colSpan={10} style={{ padding: '18px', textAlign: 'center', color: 'var(--crm-dim)', fontSize: '12px' }}>No payments yet.</td>
              </tr>
            )}
            {pays.map((p) => {
              const isRefund = p.payment_type === 'refund';
              const ccFee = p.cc_fee_amount != null ? Number(p.cc_fee_amount) : null;
              const totalCharged = ccFee != null ? Number(p.amount) + ccFee : null;
              return (
                <tr key={p.id}>
                  <Td>{formatDate(p.paid_on)}</Td>
                  <Td>
                    <span style={{ color: isRefund ? 'var(--crm-red)' : 'var(--crm-text)' }}>{humanize(p.payment_type)}</span>
                  </Td>
                  <Td>{p.method || '—'}</Td>
                  <Td align="right" mono style={{ color: isRefund ? 'var(--crm-red)' : 'var(--crm-text)' }}>
                    {isRefund ? '−' : ''}{formatMoney(p.amount)}
                  </Td>
                  <Td align="right" mono>{ccFee != null ? formatMoney(ccFee) : '—'}</Td>
                  <Td align="right" mono>{totalCharged != null ? formatMoney(totalCharged) : '—'}</Td>
                  <Td align="right" mono>
                    <SquareCostCell payment={p} isAdmin={isAdmin} onEnter={() => setSquareFor(p)} />
                  </Td>
                  <Td>{p.notes || '—'}</Td>
                  <Td>{userName(p.entered_by)}</Td>
                  <Td>
                    <span style={{ display: 'inline-flex', gap: '8px' }}>
                      {/* Editing + deleting a payment record are admin-only (N3);
                          adding a payment stays open to everyone. */}
                      {isAdmin && <button onClick={() => setPayModal({ mode: 'edit', payment: p })} title="Edit" aria-label="Edit payment" style={iconBtn}><Icons.edit size={15} /></button>}
                      {isAdmin && <button onClick={() => deletePayment(p.id)} title="Delete" aria-label="Delete payment" style={{ ...iconBtn, color: 'var(--crm-red)' }}><Icons.delete size={15} /></button>}
                    </span>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ============ INVOICES ============ */}
      {/* The invoice list (void, send, mark paid, preview). Issuing a new one is
          the Finalize draft above, never a bare generate. */}
      <DocumentsSection
        leadId={leadId}
        documents={docs}
        templates={[]}
        isAdmin={isAdmin}
        userName={userName}
        refetch={refetchDocuments}
        refetchPayments={refetchPayments}
        scope="invoices"
      />

      {payModal && (
        <PaymentModal
          mode={payModal.mode}
          payment={payModal.payment}
          methods={clientMethods}
          ccRate={ccRate}
          squareRate={squareRate}
          squareFixed={squareFixed}
          // The "Balance owing" above, once there is an invoice to owe on.
          balanceDue={usingInvoice && balance > 0 ? Math.round(balance * 100) / 100 : null}
          onSave={savePayment}
          onCancel={() => setPayModal(null)}
        />
      )}

      {squareFor && (
        <SquareCostModal
          payment={squareFor}
          prefill={squarePrefill(Number(squareFor.amount) + Number(squareFor.cc_fee_amount ?? 0), squareRate, squareFixed)}
          busy={busy}
          onSave={async (cost) => {
            setBusy(true); setSendMsg(null);
            try {
              await api.setPaymentSquareCost(squareFor.id, cost);
              setSquareFor(null);
              setSendMsg({ ok: true, text: "Square's cost recorded (Processor Fee expense)." });
              refetchPayments();
            } catch (e: any) {
              setSendMsg({ ok: false, text: e?.message || "Could not record Square's cost." });
              throw e;
            } finally { setBusy(false); }
          }}
          onCancel={() => setSquareFor(null)}
        />
      )}

    </div>
  );
}

const iconBtn: React.CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--crm-muted)', fontSize: '13px', padding: 0 };

// ---- Square's cost (048) ------------------------------------------------------

/** The prefill: charged × rate % + fixed. Only a starting point — the figure from Square is what is recorded. */
const squarePrefill = (charged: number, rate: number, fixed: number) =>
  Math.round((charged * (rate / 100) + fixed + Number.EPSILON) * 100) / 100;

/** Is this payment method a credit card? Mirrors payments.ts computeCcFee. */
const isCreditCardMethod = (m: string | null | undefined) => String(m ?? '').trim().replace(/\s+/g, ' ').toLowerCase() === 'credit card';

/** Paid by credit card, fee charged or not — Square charges us on every one (lib/squareCost.ts). */
const isCardPayment = (p: any) => p.payment_type !== 'refund' && (isCreditCardMethod(p.method) || Number(p.cc_fee_amount ?? 0) > 0);

function SquareCostCell({ payment: p, isAdmin, onEnter }: { payment: any; isAdmin: boolean; onEnter: () => void }) {
  if (!isCardPayment(p)) return <>—</>;
  if (p.square_cost != null) {
    return (
      <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', justifyContent: 'flex-end' }}>
        {formatMoney(p.square_cost)}
        {isAdmin && <button onClick={onEnter} title="Change Square's cost" aria-label="Change Square's cost" style={iconBtn}><Icons.edit size={13} /></button>}
      </span>
    );
  }
  if (p.square_cost_source === 'legacy_expense') return <span style={{ color: 'var(--crm-dim)', fontFamily: 'inherit' }} title="Booked earlier as a hand-entered processor expense">in expenses</span>;
  if (p.square_cost_source === 'not_tracked') return <span style={{ color: 'var(--crm-dim)' }} title="Recorded before Square costs were tracked">not tracked</span>;
  return (
    <button
      onClick={onEnter}
      title="Enter what Square charged for this payment. The job will not close until it is recorded."
      style={{ padding: '2px 8px', background: 'var(--crm-amber-tint)', border: '1px solid var(--crm-amber-soft-line)', color: 'var(--crm-amber)', borderRadius: '5px', cursor: 'pointer', fontSize: '10px', fontWeight: 700 }}
    >
      Enter cost
    </button>
  );
}

function SquareCostModal({ payment, prefill, busy, onSave, onCancel }: {
  payment: any; prefill: number; busy: boolean; onSave: (cost: number) => Promise<void>; onCancel: () => void;
}) {
  const [value, setValue] = useState<string>(payment.square_cost != null ? String(payment.square_cost) : prefill.toFixed(2));
  const n = Number(value);
  const ok = value.trim() !== '' && Number.isFinite(n) && n >= 0;
  const charged = Number(payment.amount) + Number(payment.cc_fee_amount ?? 0);
  const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '6px', color: 'var(--crm-text)', fontSize: '13px', padding: '8px 10px' };
  return (
    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '12px', padding: '20px', width: '340px', boxShadow: '0 20px 60px var(--crm-shadow-color)' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--crm-text)', marginBottom: '6px' }}>Square's cost</div>
        <div style={{ fontSize: '12px', color: 'var(--crm-muted)', marginBottom: '12px' }}>
          Card payment of {formatMoney(charged)} on {formatDate(payment.paid_on)}. Enter what Square charged (from Square's report); it is booked as a Processor Fee expense on this job.
        </div>
        <input {...numberInput(1)} min={0} value={value} autoFocus onChange={(e) => setValue(e.target.value)} style={input} />
        <div style={{ fontSize: '11px', color: 'var(--crm-dim)', margin: '6px 0 14px' }}>Prefilled at the Settings rate: {formatMoney(prefill)}.</div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '8px 14px', background: 'var(--crm-panel-2)', color: 'var(--crm-muted)', border: '1px solid var(--crm-line)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
          <ActionButton onAction={() => onSave(Math.round(n * 100) / 100)} onError={() => {}} disabled={!ok || busy} pendingLabel="Saving…"
            style={{ padding: '8px 16px', background: ok ? 'var(--crm-accent-fill)' : 'var(--crm-line)', color: 'var(--crm-on-fill)', border: 'none', borderRadius: '6px', cursor: ok ? 'pointer' : 'default', fontSize: '12px' }}>
            Record cost
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

function SummaryCell({ label, value, valueColor = 'var(--crm-text)', big = false }: { label: string; value: string; valueColor?: string; big?: boolean }) {
  return (
    <div style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '9px', padding: '12px 14px' }}>
      <div style={{ fontSize: '9px', fontWeight: 700, color: 'var(--crm-dim)', textTransform: 'uppercase', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: big ? '20px' : '16px', fontWeight: 700, color: valueColor, fontFamily: "'DM Mono', monospace" }}>{value}</div>
    </div>
  );
}

function Td({ children, align = 'left', mono = false, style }: { children: React.ReactNode; align?: 'left' | 'right'; mono?: boolean; style?: React.CSSProperties }) {
  return (
    <td style={{ padding: '9px 10px', fontSize: '12px', color: 'var(--crm-text)', borderBottom: '1px solid var(--crm-panel-2)', textAlign: align, fontFamily: mono ? "'DM Mono', monospace" : 'inherit', ...style }}>
      {children}
    </td>
  );
}

// ---- Add / Edit payment modal ----------------------------------------------

function PaymentModal({
  mode,
  payment,
  methods,
  ccRate,
  squareRate,
  squareFixed,
  balanceDue,
  onSave,
  onCancel,
}: {
  mode: 'add' | 'edit';
  payment?: any;
  methods: string[];
  ccRate: number;
  squareRate: number;
  squareFixed: number;
  balanceDue: number | null;
  onSave: (data: any, id: string | undefined, idempotencyKey: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [type, setType] = useState<string>(payment?.payment_type ?? 'deposit');
  // A new PAYMENT starts at the balance owing (an editable prefill); a deposit or
  // refund starts empty, as does a job with no invoice or nothing owing. The
  // prefill follows the type until the rep types an amount.
  const prefillFor = (t: string) => (mode === 'add' && t === 'payment' && balanceDue ? balanceDue.toFixed(2) : '');
  const [amount, setAmount] = useState<string>(payment?.amount != null ? String(payment.amount) : prefillFor(payment?.payment_type ?? 'deposit'));
  const [amountTouched, setAmountTouched] = useState<boolean>(mode === 'edit');
  const [method, setMethod] = useState<string>(payment?.method ?? methods[0]);
  const [paidOn, setPaidOn] = useState<string>(payment?.paid_on ?? today());
  const [notes, setNotes] = useState<string>(payment?.notes ?? '');
  const [ccApplied, setCcApplied] = useState<boolean>(payment ? payment.cc_fee_amount != null : true);
  const [sendReceipt, setSendReceipt] = useState<boolean>(true); // E4 Item 4 — checked by default

  // Case- and space-tolerant: the client list (Settings) is seeded 'Credit Card',
  // older lists and payments say 'Credit card'. An exact match against one
  // spelling hid the CC fee and the Square cost prompt for the other.
  const isCard = isCreditCardMethod(method);
  const showCc = isCard && type !== 'refund';
  const amt = parseFloat(amount) || 0;
  const feeAmt = showCc && ccApplied ? Math.round(amt * (ccRate / 100) * 100) / 100 : 0;
  const totalCharged = amt + feeAmt;
  // 048: Square's cost on a card payment. Follows the prefill (charged × rate %
  // + fixed) until it is typed over; then it is the rep's figure.
  const [squareText, setSquareText] = useState<string>(payment?.square_cost != null ? String(payment.square_cost) : '');
  const [squareTouched, setSquareTouched] = useState<boolean>(payment?.square_cost != null);
  const squarePre = amt > 0 ? squarePrefill(totalCharged, squareRate, squareFixed).toFixed(2) : '';
  // Editing a payment that has no cost (or one covered by a legacy expense)
  // shows the prefill only as a hint: sending it would book a cost nobody typed.
  const squareShown = squareTouched ? squareText : (mode === 'add' ? squarePre : '');
  const squareNum = Number(squareShown);
  const squareOk = squareShown.trim() !== '' && Number.isFinite(squareNum) && squareNum >= 0;

  // The modal's ActionButton owns the key: one opening of the modal is one intent.
  const submit = (key: string) => {
    if (amt <= 0) return Promise.resolve();
    return onSave(
      {
        amount: amt,
        payment_type: type,
        method,
        paid_on: paidOn,
        notes,
        cc_fee_applied: showCc && ccApplied,
        // Left blank, the payment is "Square cost needed" until entered.
        // Fee box checked or not: Square charges us on every card payment.
        ...(showCc && squareOk ? { square_cost: Math.round(squareNum * 100) / 100 } : {}),
        // Receipt only on a new, non-refund payment (edits don't re-send).
        send_receipt: mode !== 'edit' && type !== 'refund' && sendReceipt,
      },
      payment?.id,
      key
    );
  };

  const label: React.CSSProperties = { fontSize: '10px', fontWeight: 600, color: 'var(--crm-muted)', textTransform: 'uppercase', display: 'block', marginBottom: '5px' };
  const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '6px', color: 'var(--crm-text)', fontSize: '13px', padding: '8px 10px' };

  return (
    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '12px', padding: '20px', width: '360px', maxWidth: '92vw', maxHeight: '90dvh', overflowY: 'auto', boxSizing: 'border-box', boxShadow: '0 20px 60px var(--crm-shadow-color)' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--crm-text)', marginBottom: '16px' }}>{mode === 'edit' ? 'Edit payment' : 'Add payment'}</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
          <div>
            <label style={label}>Type</label>
            <select value={type} onChange={(e) => { setType(e.target.value); if (!amountTouched) setAmount(prefillFor(e.target.value)); }} style={input}>
              <option value="deposit">Deposit</option>
              <option value="payment">Payment</option>
              <option value="refund">Refund</option>
            </select>
          </div>
          <div>
            <label style={label}>Amount</label>
            <input {...numberInput(1)} value={amount} autoFocus onChange={(e) => { setAmountTouched(true); setAmount(e.target.value); }} style={input} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
          <div>
            <label style={label}>Method</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)} style={input}>
              {methods.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={label}>Date</label>
            <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} style={input} />
          </div>
        </div>

        <div style={{ marginBottom: '10px' }}>
          <label style={label}>Notes</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} style={input} />
        </div>

        {showCc && (
          <div style={{ background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '8px', padding: '11px', marginBottom: '14px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: 'var(--crm-text)' }}>
              <input type="checkbox" checked={ccApplied} onChange={(e) => setCcApplied(e.target.checked)} />
              Add CC Processing Fee ({ccRate}%)
            </label>
            {ccApplied && (
              <div style={{ marginTop: '10px', fontSize: '12px', fontFamily: "'DM Mono', monospace" }}>
                <PreviewRow label="Payment amount" value={formatMoney(amt)} />
                <PreviewRow label={`CC fee (${ccRate}%)`} value={formatMoney(feeAmt)} color="var(--crm-amber)" />
                <div style={{ borderTop: '1px solid var(--crm-line)', marginTop: '6px', paddingTop: '6px' }}>
                  <PreviewRow label="Total charged" value={formatMoney(totalCharged)} bold />
                </div>
              </div>
            )}
            {/* Shown whether or not the fee is added: the prefill is on the total
                charged, which is the amount alone when it is not. */}
              <div style={{ marginTop: '10px' }}>
                <label style={label}>Square's cost</label>
                <input
                  {...numberInput(1)} min={0}
                  value={squareShown}
                  placeholder={squarePre}
                  onChange={(e) => { setSquareTouched(true); setSquareText(e.target.value); }}
                  style={input}
                />
                {mode === 'edit' && payment?.square_cost == null && payment?.square_cost_source && (
                  <div style={{ fontSize: '10px', color: 'var(--crm-amber)', marginTop: '4px' }}>
                    {payment.square_cost_source === 'legacy_expense' ? 'Already covered by a hand-entered processor expense' : 'Marked not tracked (history)'} — leave blank unless replacing that.
                  </div>
                )}
                <div style={{ fontSize: '10px', color: 'var(--crm-dim)', marginTop: '4px' }}>
                  Prefilled {squareRate}% + {formatMoney(squareFixed)} of the charged total — change it to what Square actually charged. Booked as a Processor Fee expense. Left blank, the job shows "Square cost needed" and will not close.
                </div>
              </div>
          </div>
        )}

        {mode !== 'edit' && type !== 'refund' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: 'var(--crm-text)', marginBottom: '14px' }}>
            <input type="checkbox" checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} />
            Email receipt to the client
          </label>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '8px 14px', background: 'var(--crm-panel-2)', color: 'var(--crm-muted)', border: '1px solid var(--crm-line)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
          <ActionButton onAction={submit} onError={() => {}} disabled={amt <= 0} pendingLabel="Saving…"
            style={{ padding: '8px 16px', background: amt > 0 ? 'var(--crm-accent-fill)' : 'var(--crm-line)', color: 'var(--crm-on-fill)', border: 'none', borderRadius: '6px', cursor: amt > 0 ? 'pointer' : 'default', fontSize: '12px' }}>
            {mode === 'edit' ? 'Save' : 'Add payment'}
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

function PreviewRow({ label, value, color = 'var(--crm-text)', bold = false }: { label: string; value: string; color?: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
      <span style={{ color: 'var(--crm-muted)' }}>{label}</span>
      <span style={{ color, fontWeight: bold ? 700 : 400 }}>{value}</span>
    </div>
  );
}
